import type { PlacementFailure } from "../shared/protocol";

export type Language = "sl" | "en";
export const LANGUAGE_KEY = "games-language";

type Copy = {
  back: string;
  title: string;
  subtitle: string;
  help: string;
  languageLabel: string;
  connection: Record<"connecting" | "online" | "offline", string>;
  stageAria: string;
  canvasAria: string;
  round: string;
  towerTitle: string;
  bridgeTitle: string;
  archipelagoTitle: string;
  hangingTitle: string;
  archipelagoTip: string;
  hangingTip: string;
  difficulty: string;
  targets: string;
  target: string;
  connectAll: string;
  buildDown: string;
  overview: string;
  assistance: string;
  eased: string;
  goal: string;
  statsAria: string;
  gooCount: string;
  builders: string;
  moving: string;
  towerTip: string;
  bridgeTip: string;
  groundError: string;
  toolsAria: string;
  build: string;
  pan: string;
  stress: string;
  tension: string;
  compression: string;
  zoomOut: string;
  zoomIn: string;
  focusTower: string;
  focusBridge: string;
  focusDown: string;
  reset: string;
  resetVoteAria: string;
  resetRemoveAria: string;
  loadingTitle: string;
  loadingBody: string;
  victoryEyebrow: string;
  victoryReached: string;
  victoryBy: (name: string) => string;
  nextMap: string;
  seconds: string;
  close: string;
  helpTitle: string;
  helpBuild: string;
  helpPan: string;
  helpStress: string;
  helpServer: string;
  helpNote: string;
  newTower: string;
  newBridge: string;
  goalTower: string;
  chasm: string;
  foundation: string;
  support: string;
  addedBy: string;
  winLine: string;
  otherSide: string;
  invalidServerMessage: string;
  noticeUnavailable: string;
  noticeStarting: string;
  coordinateError: string;
  webStatusTitle: string;
  webStatusDescription: string;
  webPlaceTitle: string;
  webPlaceDescription: string;
  failures: Record<PlacementFailure, string>;
  nickFirst: readonly string[];
  nickSecond: readonly string[];
};

export const COPY: Record<Language, Copy> = {
  sl: {
    back: "Nazaj na games.ddsq.si",
    title: "Goo stolp",
    subtitle: "en stolp · vsi igralci",
    help: "Kako se igra?",
    languageLabel: "Izberi jezik",
    connection: { connecting: "Povezujem …", online: "Skupni svet", offline: "Ponovna povezava …" },
    stageAria: "Skupni Goo stolp",
    canvasAria: "Igralno območje. Izberi Gradi in klikni blizu stolpa.",
    round: "Runda",
    towerTitle: "Stolp do neba",
    bridgeTitle: "Most čez prepad",
    archipelagoTitle: "Gozdni otoki",
    hangingTitle: "Viseči vrt",
    archipelagoTip: "Poveži vse rdeče cilje z začetnim otokom. Bele opore pomagajo šele, ko jih doseže konstrukcija. Zemljevid pokaže celo sobo.",
    hangingTip: "Začni pri črnih sidrih pod zgornjim otokom in gradi navzdol do rdečih ciljev. Izkoristi bele opore za razbremenitev vezi.",
    difficulty: "Stopnja",
    targets: "cilji",
    target: "CILJ",
    connectAll: "Poveži vse otoke",
    buildDown: "Gradi navzdol",
    overview: "Zemljevid",
    assistance: "dodatne opore",
    eased: "Teden brez zmage: lažja stopnja in dodatne opore. Gradnja je ohranjena.",
    goal: "Cilj: 200 m",
    statsAria: "Stanje stolpa",
    gooCount: "Gooji",
    builders: "Graditelji",
    moving: "V gibanju",
    towerTip: "Gradi od temelja do rdeče črte. Bele opore na približno 45 m se izmenjujejo levo in desno; uporabne so šele, ko jih doseže stolp.",
    bridgeTip: "Gradi z leve do belih vmesnih opor in naprej do rdečega cilja. Bele opore ne morejo začeti mostu.",
    groundError: "Goo ne more biti v zemlji.",
    toolsAria: "Orodja",
    build: "Gradi",
    pan: "Razgled",
    stress: "Stress",
    tension: "Nateg",
    compression: "Tlak",
    zoomOut: "Pomanjšaj",
    zoomIn: "Povečaj",
    focusTower: "Na vrh ↑",
    focusBridge: "Na rob →",
    focusDown: "Navzdol ↓",
    reset: "Nova soba",
    resetVoteAria: "Glasuj za novo naključno sobo",
    resetRemoveAria: "Umakni glas za novo naključno sobo",
    loadingTitle: "Prebujam skupni stolp",
    loadingBody: "Fiziko računa strežnik.",
    victoryEyebrow: "SKUPNA ZMAGA",
    victoryReached: "Cilj dosežen!",
    victoryBy: (name) => `${name} je zaključil cilj!`,
    nextMap: "Nov zemljevid čez",
    seconds: "s",
    close: "Zapri",
    helpTitle: "Skupaj gradimo en stolp.",
    helpBuild: "klikni ali tapni prazno mesto blizu obstoječega goo-ja.",
    helpPan: "povleci prizor ali pomakni miško do roba; s koleščkom ali gumboma pa približaj.",
    helpStress: "vklopi Stress za zvezni prikaz sil v vezeh: modra je nateg, rdeča je tlak. Bele opore postanejo uporabne šele, ko jih doseže konstrukcija od začetka.",
    helpServer: "Postavitev potrdi strežnik in jo takoj pokaže vsem igralcem.",
    helpNote: "Soba sprejme največ 2.000 goo-jev. Poveži vse rdeče cilje ali dosezi ciljno višino. Zmaga zviša stopnjo; teden brez zmage jo skoraj prepolovi in doda opore brez brisanja gradnje. Vezi se lahko pretrgajo. Zemljevid se ponastavi, ko se strinja vsaj 50 % prisotnih igralcev.",
    newTower: "Nova runda: dosežimo rdečo črto!",
    newBridge: "Nova runda: povežimo obe strani prepada!",
    goalTower: "Cilj: rdeča črta pri",
    chasm: "Prepad",
    foundation: "Temelj stolpa",
    support: "Vmesna opora",
    addedBy: "Dodal",
    winLine: "ZMAGA",
    otherSide: "CILJ · DRUGA STRAN",
    invalidServerMessage: "Neveljavno sporočilo strežnika",
    noticeUnavailable: "Fizikalni svet trenutno ni na voljo.",
    noticeStarting: "Skupni svet se zaganja …",
    coordinateError: "x in y morata biti števili",
    webStatusTitle: "Preberi stanje Goo stolpa",
    webStatusDescription: "Vrne trenutno višino, število goo-jev, povezav in prisotnih igralcev.",
    webPlaceTitle: "Postavi goo",
    webPlaceDescription: "Postavi nov goo največ 2,8 m od konstrukcije. Poveže se z do petimi najbližjimi sosedi. Tla so pri y=0, višje po stolpu pa so negativne vrednosti y.",
    failures: {
      invalid_position: "To mesto je zunaj sveta.",
      world_full: "Stolp je dosegel omejitev 2.000 goo-jev.",
      too_close: "Malo več prostora — goo-ja se prekrivata.",
      too_far: "Postavi ga bližje obstoječemu stolpu.",
      rate_limited: "Počasi — stolp potrebuje trenutek.",
      level_complete: "Ta cilj je že dosežen. Naslednji zemljevid prihaja.",
      server_starting: "Strežnik se še prebuja. Poskusi čez trenutek.",
    },
    nickFirst: ["Mirni", "Zeleni", "Mehki", "Pogumni", "Veseli"],
    nickSecond: ["Goo", "Graditelj", "Mojster", "Plezalec"],
  },
  en: {
    back: "Back to games.ddsq.si",
    title: "Goo Tower",
    subtitle: "one tower · every player",
    help: "How to play",
    languageLabel: "Choose language",
    connection: { connecting: "Connecting …", online: "Shared world", offline: "Reconnecting …" },
    stageAria: "Shared Goo Tower",
    canvasAria: "Game area. Choose Build and click near the tower.",
    round: "Round",
    towerTitle: "Tower to the sky",
    bridgeTitle: "Bridge the chasm",
    archipelagoTitle: "Forest islands",
    hangingTitle: "Hanging garden",
    archipelagoTip: "Connect every red target to the starting island. White supports only help once your structure reaches them. Map reveals the whole room.",
    hangingTip: "Start at the black anchors under the upper island and build down to the red targets. Use white supports to relieve tension.",
    difficulty: "Level",
    targets: "targets",
    target: "TARGET",
    connectAll: "Connect all islands",
    buildDown: "Build downwards",
    overview: "Map",
    assistance: "extra supports",
    eased: "A week without a win: lower difficulty and extra supports. Your structure is preserved.",
    goal: "Goal: 200 m",
    statsAria: "Tower status",
    gooCount: "Goos",
    builders: "Builders",
    moving: "Moving",
    towerTip: "Build from the base to the red line. White supports about every 45 m alternate left and right; they only help once the tower reaches them.",
    bridgeTip: "Build from the left to the white supports, then on to the red goal. A white support cannot start the bridge.",
    groundError: "A Goo cannot be placed inside the ground.",
    toolsAria: "Tools",
    build: "Build",
    pan: "Explore",
    stress: "Stress",
    tension: "Tension",
    compression: "Compression",
    zoomOut: "Zoom out",
    zoomIn: "Zoom in",
    focusTower: "To top ↑",
    focusBridge: "To edge →",
    focusDown: "To bottom ↓",
    reset: "New room",
    resetVoteAria: "Vote for a new random room",
    resetRemoveAria: "Withdraw your vote for a new random room",
    loadingTitle: "Waking the shared tower",
    loadingBody: "Physics runs on the server.",
    victoryEyebrow: "SHARED VICTORY",
    victoryReached: "Goal reached!",
    victoryBy: (name) => `${name} completed the goal!`,
    nextMap: "New map in",
    seconds: "s",
    close: "Close",
    helpTitle: "We are building one tower together.",
    helpBuild: "click or tap an empty spot near an existing Goo.",
    helpPan: "drag the scene or move the pointer to an edge; zoom with the wheel or the buttons.",
    helpStress: "turn on Stress for a continuous force scale: blue means tension, red means compression. White supports only help after the structure reaches them from its starting point.",
    helpServer: "The server confirms each placement and immediately shows it to every player.",
    helpNote: "A room holds up to 2,000 Goos. Connect every red target or reach the target height. Wins raise the level; a week without a win roughly halves it and adds supports without deleting your structure. Links can snap. The map resets when at least 50% of connected players agree.",
    newTower: "New round: reach the red line!",
    newBridge: "New round: let’s connect both sides of the chasm!",
    goalTower: "Goal: red line at",
    chasm: "Chasm",
    foundation: "Tower foundation",
    support: "Intermediate support",
    addedBy: "Added by",
    winLine: "VICTORY",
    otherSide: "GOAL · OTHER SIDE",
    invalidServerMessage: "Invalid server message",
    noticeUnavailable: "The physics world is temporarily unavailable.",
    noticeStarting: "The shared world is starting …",
    coordinateError: "x and y must be numbers",
    webStatusTitle: "Read Goo Tower status",
    webStatusDescription: "Returns the current height, Goo count, connection count and number of players.",
    webPlaceTitle: "Place a Goo",
    webPlaceDescription: "Place a new Goo within 2.8 m of the structure. It connects to up to five nearest neighbours. Ground is at y=0 and higher tower positions use negative y values.",
    failures: {
      invalid_position: "That position is outside the world.",
      world_full: "The tower has reached the 2,000-Goo limit.",
      too_close: "Leave a little more room — the Goos overlap.",
      too_far: "Place it closer to the existing structure.",
      rate_limited: "Slow down — the tower needs a moment.",
      level_complete: "This goal is already complete. The next map is coming.",
      server_starting: "The server is still waking up. Try again in a moment.",
    },
    nickFirst: ["Calm", "Green", "Soft", "Brave", "Happy"],
    nickSecond: ["Goo", "Builder", "Master", "Climber"],
  },
};
